import { AppLauncher } from '@capacitor/app-launcher';
import { Capacitor } from '@capacitor/core';
import * as Sentry from '@sentry/capacitor';

import { ILatLng } from '@mopla/data-models';

const PLATFORM = Capacitor.getPlatform();

interface IOption {
	/** Native application (maps) link to check if an application is able to be opened */
	checkLink: string | null;
	/** Native application (maps) link to open a map and show a certain point */
	buildAppLink: (latLng: ILatLng) => string;
	/** Native application (maps) link to open a turn-by-turn route */
	buildRouteLink: (from: ILatLng, to: ILatLng) => string;
	/** Native application (maps) link to start turn-by-turn navigation to a destination using current position */
	buildNavigationLink: (to: ILatLng) => string;
}

type TOptionKey = 'ios_gmaps' | 'android_gmaps' | 'ios_native_maps' | 'web';

const configs: Record<TOptionKey, IOption> = {
	ios_gmaps: {
		checkLink: 'comgooglemaps-x-callback://',
		buildAppLink: (latLng: ILatLng) =>
			`comgooglemaps-x-callback://?q=${latLng.lat},${latLng.lng}&x-success=sourceapp://?resume=true&x-source=AirApp`,
		buildRouteLink: (from: ILatLng, to: ILatLng) =>
			`comgooglemaps-x-callback://?saddr=${from.lat},${from.lng}&daddr=${to.lat},${to.lng}&directionsmode=driving&x-success=sourceapp://?resume=true&x-source=AirApp`,
		buildNavigationLink: (to: ILatLng) =>
			`https://www.google.com/maps/dir/?api=1&destination=${to.lat},${to.lng}&travelmode=driving&dir_action=navigate`,
	},
	android_gmaps: {
		checkLink: 'com.google.android.apps.maps',
		buildAppLink: (latLng: ILatLng) => `geo:0,0?q=${latLng.lat},${latLng.lng}`,
		buildRouteLink: (from: ILatLng, to: ILatLng) =>
			`https://www.google.com/maps/dir/?api=1&origin=${from.lat},${from.lng}&destination=${to.lat},${to.lng}&travelmode=driving`,
		buildNavigationLink: (to: ILatLng) =>
			`https://www.google.com/maps/dir/?api=1&destination=${to.lat},${to.lng}&travelmode=driving&dir_action=navigate`,
	},
	ios_native_maps: {
		checkLink: 'maps://',
		buildAppLink: (latLng: ILatLng) =>
			`maps://?q=${latLng.lat},${latLng.lng}&x-success=sourceapp://?resume=true&x-source=AirApp`,
		buildRouteLink: (from: ILatLng, to: ILatLng) =>
			`maps://?saddr=${from.lat},${from.lng}&daddr=${to.lat},${to.lng}&dirflg=d`,
		buildNavigationLink: (to: ILatLng) =>
			`maps://?daddr=${to.lat},${to.lng}&dirflg=d`,
	},
	web: {
		checkLink: null,
		buildAppLink: (latLng: ILatLng) =>
			`https://www.google.com/maps/search/?api=1&query=${latLng.lat},${latLng.lng}`,
		buildRouteLink: (from: ILatLng, to: ILatLng) =>
			`https://www.google.com/maps/dir/?api=1&origin=${from.lat},${from.lng}&destination=${to.lat},${to.lng}&travelmode=driving`,
		buildNavigationLink: (to: ILatLng) =>
			`https://www.google.com/maps/dir/?api=1&destination=${to.lat},${to.lng}&travelmode=driving&dir_action=navigate`,
	},
};

const getPlatformOptions = (): IOption[] => {
	if (PLATFORM === 'android') return [configs.android_gmaps];
	if (PLATFORM === 'ios') return [configs.ios_gmaps, configs.ios_native_maps];
	return [];
};

const findAvailableOption = async (
	options: IOption[]
): Promise<IOption | null> => {
	for (const option of options) {
		if (!option.checkLink) continue;

		const { value } = await AppLauncher.canOpenUrl({ url: option.checkLink });

		if (value) return option;
	}

	return null;
};

/** Open provided latLng point in a maps application (native) or google maps (web) */
export const openMapsApp = async (latLng: ILatLng) => {
	let appLink: string = configs.web.buildAppLink(latLng);

	try {
		const option = await findAvailableOption(getPlatformOptions());

		if (option) appLink = option.buildAppLink(latLng);
	} catch (e) {
		Sentry.captureException(e);
	}

	await AppLauncher.openUrl({ url: appLink });
};

/** Open a turn-by-turn route between two points in a maps application */
export const openMapsRoute = async (from: ILatLng, to: ILatLng) => {
	let routeLink: string = configs.web.buildRouteLink(from, to);

	try {
		const option = await findAvailableOption(getPlatformOptions());

		if (option) routeLink = option.buildRouteLink(from, to);
	} catch (e) {
		Sentry.captureException(e);
	}

	await AppLauncher.openUrl({ url: routeLink });
};

/** Start turn-by-turn navigation to a destination using current device position as origin */
export const openMapsNavigation = async (to: ILatLng) => {
	let navLink: string = configs.web.buildNavigationLink(to);

	try {
		const option = await findAvailableOption(getPlatformOptions());

		if (option) navLink = option.buildNavigationLink(to);
	} catch (e) {
		Sentry.captureException(e);
	}

	await AppLauncher.openUrl({ url: navLink });
};

/**
 * Wrapper function for setting window.location.href (useful for testing)
 * @see https://github.com/jsdom/jsdom/issues/3492
 * */
export const setWindowLocationHref = (url: string): void => {
	window.location.href = url;
};
